/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {downloadFile, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import VatDeclarationItem from '../../models/vatDeclarationItemModel';
import {
    DataSplitMethod,
    ExportProcessStatus,
    InvoiceExportType,
    ProcessingStatus,
    ProcessType,
    VatDeclarationInvalidation
} from '../../util/interfaces';
import {AppContext} from '../misc/appContext';
import {downloadBolInvoice, exportVatDeclaration, getDefaultVats, getVatDeclaration} from '../../util/requestSender';
import {PRODUCTS_ROUTE} from '../../util/constants';
import {trackPlatformAction} from '../../util/analytics';
import {createErrorMessage, onNavigate} from '../../util/utils';
import VatDeclaration from './vatDeclaration';
import {vatDeclarationIsInvalidated} from '../../util/vatDeclarationUtil';
import ProcessStatus from '../../models/processStatusModel';
import {onSetCrumbs} from '../../util/events';
import FirstUseNotifier from './firstUseNotifier';
import ExportProcess from '../../models/exportProcessModel';
import Account from '../../models/accountModel';
import {useIsFirstRender} from '../../util/componentUtil';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const dateFormat = 'YYYY-MM-DD';
const LOAD_PROCESS_TIMEOUT = 1000;

const mailKey = 'notify_vat_declaration_finished';
const periodKey = 'vat_declaration_period';

type LoadConfig = {
    useAPI?: boolean;
    recalculate?: boolean;
    period?: {start: IDate; end: IDate} | null;
    callback?: VoidFunction;
    accountID?: string;
};

export default function VatDeclarationController() {
    const context = useContext(AppContext);
    const {account, accounts} = context.accountContext;
    const {t} = useTranslation();
    const [declarationItems, setDeclarationItems] = useState<Record<string, VatDeclarationItem[]>>({});
    const [period, setPeriod] = useState<{start: IDate; end: IDate} | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);
    const [processStatus, setProcessStatus] = useState<ProcessStatus | null>(null);
    const [showInfo, setShowInfo] = useState(context.userContext.user?.getFlex('vat_decl_info_closed') !== true);
    const [defaultVats, setDefaultVats] = useState<Record<string, number>>({});
    const [exportProcesses, setExportProcesses] = useState<Array<ExportProcess>>([]);
    const invalidated = useMemo<VatDeclarationInvalidation | null>(
        () => vatDeclarationIsInvalidated(declarationItems),
        [declarationItems]
    );
    const loadProcessTimer = useRef<number | null>(null);
    const loadExportProcessTimer = useRef<number | null>(null);
    const noReset = useRef(false);
    const isFirstRender = useIsFirstRender();

    function onToggleMail() {
        if (!processStatus) return;
        const toggled = !processStatus.data[mailKey];
        processStatus.data[mailKey] = toggled;
        const dateFormat = 'DD-MM-YYYY';
        processStatus.data[periodKey] = {start: period?.start.format(dateFormat), end: period?.end.format(dateFormat)};
        processStatus.update((processStatus: ProcessStatus, success: boolean, data: ResponseData) => {
            if (success) {
                setProcessStatus(processStatus);
                if (toggled)
                    showSnackbar(t('vatDeclaration.mailEnabled', 'Je krijgt een mail zodra de berekening voltooid is'));
                else
                    showSnackbar(
                        t('vatDeclaration.mailDisabled', 'Je krijgt geen mail meer zodra de berekening voltooid is')
                    );
            } else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t(
                                'vatDeclaration.error.notificationFailed',
                                'Het instellen van de notificatie is niet gelukt.'
                            )
                    ),
                    t('vatDeclaration.error.notificationFailedTitle', 'Instellen mislukt')
                );
        });
    }

    function onChangePeriod(start: IDate, end: IDate, isCustom: boolean = false) {
        const period = {start, end};
        setPeriod(period);
        onPeriodChange(period, isCustom);
    }

    function onPeriodChange(period: {start: IDate; end: IDate}, isCustom: boolean) {
        const recalculate = isCustom || invalidated !== null;
        onFetch({recalculate, period});
    }

    function onCloseInfo() {
        setShowInfo(false);
        const {user} = context.userContext;
        if (!user) return;
        user.setFlex('vat_decl_info_closed', true);
        user.update();
    }

    function onClickCalculate(useAPI: boolean | React.MouseEvent) {
        if (useAPI !== false) useAPI = true;
        const recalculate = invalidated !== null;
        onFetch({useAPI, recalculate});
    }

    function onNavigateToProducts() {
        if (processStatus) deleteProcessAndRecalculate(false, onNavigate(PRODUCTS_ROUTE));
        else onFetch({useAPI: false, recalculate: true, callback: onNavigate(PRODUCTS_ROUTE)});
    }

    function onRecalculate() {
        setLoading(true);
        if (processStatus) deleteProcessAndRecalculate(true);
        else onFetch({recalculate: true});
    }

    function deleteProcessAndRecalculate(useAPI: boolean, callback?: VoidFunction) {
        processStatus!.delete((_: ProcessStatus, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setProcessStatus(null);
                onFetch({useAPI, recalculate: true, callback});
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('vatDeclaration.error.systemError', 'Er is iets misgegaan in ons systeem.')
                    ),
                    t('vatDeclaration.error.somethingWrong', 'Er is iets misgegaan')
                );
        });
    }

    function onFetch(config?: LoadConfig) {
        const accountID = config?.accountID ?? account?.id;
        const _period = config?.period ?? period;
        if (!accountID || !_period) return;
        setDeclarationItems({});
        const useAPI = config?.useAPI ?? true;
        const recalculate = config?.recalculate ?? false;
        setLoading(true);
        getVatDeclaration({
            data: {
                account_id: accountID,
                period_end: _period.end.format(dateFormat),
                period_start: _period.start.format(dateFormat),
                split_key: DataSplitMethod.MONTH,
                use_api: useAPI,
                recalculate
            },
            callback: (data: ResponseData) => {
                onFetchResponse(data, _period!, config?.callback);
            }
        });
    }

    function onFetchResponse(data: ResponseData, period: {start: IDate; end: IDate}, callback?: VoidFunction) {
        if (!data.success) {
            setLoading(false);
            showDialog(
                createErrorMessage(
                    data.message ??
                        t('vatDeclaration.error.calculateFailed', 'Het berekenen van de BTW aangifte is niet gelukt.')
                ),
                t('vatDeclaration.error.calculateFailedTitle', 'Berekenen mislukt')
            );
            if (callback) callback();
            return;
        }
        const {process_status, declaration_data} = data;
        if (process_status) return onProcessResponse(new ProcessStatus(process_status), period);
        else return onDataResponse(declaration_data, callback);
    }

    function onProcessResponse(processStatus: ProcessStatus, period: {start: IDate; end: IDate}) {
        setLoading(false);
        setProcessStatus(processStatus);
        loadProcessStatus(period);
    }

    function onDataResponse(data: Record<string, any>, callback?: VoidFunction) {
        for (const key of Object.keys(data)) data[key] = data[key].map((obj: object) => new VatDeclarationItem(obj));
        setDeclarationItems(data);
        setLoading(false);
        if (callback) callback();
    }

    function onDownloadInvoice(invoiceId: string, fileType: 'pdf' | 'excel') {
        return function () {
            if (!account) return;
            setLoadingExport(true);
            downloadBolInvoice({
                invoice_id: invoiceId,
                account_id: account.id,
                file_type: fileType,
                callback: (data: ResponseData, response: Response) => {
                    if (data.status === 'success') {
                        const ext = fileType === 'pdf' ? 'pdf' : 'xlsx';
                        let fileName = `${invoiceId}.${ext}`;
                        const header = response.headers.get('Content-Disposition');
                        if (header) fileName = header.split('=').pop() ?? fileName;
                        response.blob().then((blob: Blob) => {
                            if (fileType === 'pdf') {
                                const objectUrl = URL.createObjectURL(blob);
                                window.open(objectUrl, '__blank');
                                URL.revokeObjectURL(objectUrl);
                            } else downloadFile(blob, fileName);
                            setLoadingExport(false);
                        });
                    } else {
                        setLoadingExport(false);
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'vatDeclaration.error.downloadInvoiceFailed',
                                        'Het downloaden van de factuur is niet gelukt.'
                                    )
                            ),
                            t('vatDeclaration.error.downloadFailedTitle', 'Downloaden mislukt')
                        );
                    }
                }
            });
        };
    }

    function onDownloadExport(exportProcess: ExportProcess) {
        return function () {
            setLoading(true);
            if (exportProcess.status === ExportProcessStatus.FINISHED) {
                exportProcess.download(
                    () => {
                        setLoading(false);
                    },
                    (data: ResponseData) => {
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'vatDeclaration.error.downloadFileFailed',
                                        'Het downloaden van het bestand is niet gelukt.'
                                    )
                            ),
                            t('vatDeclaration.error.downloadFailedTitle', 'Downloaden mislukt')
                        );
                    }
                );
            }
        };
    }

    function onExportDeclaration() {
        showDialog(
            t(
                'vatDeclaration.export.dialogMessage',
                'Je kunt de berekening exporteren naar een csv bestand of een Excel bestand. Welk bestandstype wil je gebruiken?'
            ),
            t('vatDeclaration.export.dialogTitle', 'Berekening exporteren'),
            [
                {label: t('common.cancel', 'Annuleren'), primary: false},
                {
                    label: t('vatDeclaration.export.csv', 'CSV bestand'),
                    onClick: exportDeclaration(InvoiceExportType.VAT_DECL_CSV)
                },
                {
                    label: t('vatDeclaration.export.excel', 'Excel bestand'),
                    onClick: exportDeclaration(InvoiceExportType.VAT_DECL_EXCEL)
                }
            ]
        );
    }

    function exportDeclaration(exportType: InvoiceExportType) {
        return function () {
            if (!account || !period) return;
            setLoading(true);
            const dateFormat = 'YYYY-MM-DD';
            exportVatDeclaration({
                data: {
                    account_id: account.id,
                    export_type: exportType,
                    period_end: period.end.format(dateFormat),
                    period_start: period.start.format(dateFormat)
                },
                callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        trackPlatformAction('vat.declaration', 'export_declaration');
                        loadExportProcesses();
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'vatDeclaration.error.exportFailed',
                                        'Er is iets misgegaan bij het exporteren van de berekening.'
                                    )
                            ),
                            t('vatDeclaration.error.exportFailedTitle', 'Exporteren mislukt')
                        );
                }
            });
        };
    }

    function clearLoadTimeout() {
        if (loadProcessTimer.current !== null) {
            window.clearTimeout(loadProcessTimer.current);
            loadProcessTimer.current = null;
        }
    }

    function clearExportLoadTimeout() {
        if (loadExportProcessTimer.current !== null) {
            window.clearTimeout(loadExportProcessTimer.current);
            loadExportProcessTimer.current = null;
        }
    }

    function loadDefaultVats() {
        if (!account) return;
        getDefaultVats({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) setDefaultVats(data.default_vats);
            }
        });
    }

    function loadProcessStatus(period: {start: IDate; end: IDate} | null, fetchIfNotFound: boolean = false) {
        clearLoadTimeout();
        if (!account) return;
        ProcessStatus.getByType(account.id, ProcessType.VAT_DECLARATION, (processStatus: ProcessStatus | null) => {
            if (processStatus) {
                if (processStatus.status === ProcessingStatus.RUNNING) {
                    loadProcessTimer.current = window.setTimeout(() => {
                        loadProcessStatus(period);
                    }, LOAD_PROCESS_TIMEOUT);
                    setProcessStatus(processStatus);
                } else if (processStatus.status === ProcessingStatus.FINISHED) {
                    onFetch({useAPI: false, period});
                    setProcessStatus(null);
                } else if (processStatus.status === ProcessingStatus.WAITING) {
                    setProcessStatus(processStatus);
                }
            } else {
                setProcessStatus(null);
                if (fetchIfNotFound) onClickCalculate(true);
            }
        });
    }

    function loadExportProcesses() {
        clearExportLoadTimeout();
        if (!account || !period) return;
        const data = {
            account_id: account.id,
            export_types: [InvoiceExportType.VAT_DECL_EXCEL, InvoiceExportType.VAT_DECL_CSV],
            period_start: period.start.format(dateFormat),
            period_end: period.end.format(dateFormat)
        };
        ExportProcess.getAll(data, (exportProcesses: Array<ExportProcess>, success: boolean) => {
            if (!success) return;
            setExportProcesses(exportProcesses);
            const isRunning = exportProcesses.find(
                (process: ExportProcess) => process.status === ExportProcessStatus.EXPORTING
            );
            if (isRunning)
                loadExportProcessTimer.current = window.setTimeout(loadExportProcesses, LOAD_PROCESS_TIMEOUT);
        });
    }

    function reset() {
        console.warn('Resetting!');
        setDeclarationItems({});
        setPeriod(null);
        setLoading(false);
        setProcessStatus(null);
        loadDefaultVats();
        setExportProcesses([]);
    }

    function checkParams() {
        const params = new URLSearchParams(location.search);
        const accountID = params.get('account');
        const periodStart = params.get('period-start');
        const periodEnd = params.get('period-end');
        const useAPI = params.get('fetch') === '1';
        if (accountID) {
            if (account?.id !== accountID) {
                const account = accounts.find((account: Account) => account.id === accountID);
                if (account) {
                    noReset.current = true;
                    context.onChangeContext({account}, () => {
                        if (periodStart && periodEnd) {
                            const period = {start: new IDate(periodStart), end: new IDate(periodEnd)};
                            setPeriod(period);
                            onFetch({period, accountID, useAPI});
                        }
                    });
                }
            } else if (periodStart && periodEnd) {
                const period = {start: new IDate(periodStart), end: new IDate(periodEnd)};
                setPeriod(period);
                onFetch({period, accountID, useAPI});
            }
        }
    }

    useEffect(() => {
        if (!isFirstRender) {
            if (noReset.current) noReset.current = false;
            else {
                clearLoadTimeout();
                reset();
                loadProcessStatus(null, false);
            }
        }
        return function () {
            clearLoadTimeout();
            clearExportLoadTimeout();
        };
    }, [context.accountContext.account?.id]);

    useEffect(() => {
        onSetCrumbs([{label: t('vatDeclaration.crumb', 'Hulp bij BTW aangifte')}]);
        checkParams();
        return function () {
            clearLoadTimeout();
            clearExportLoadTimeout();
        };
    }, []);

    useEffect(() => {
        loadExportProcesses();
        return function () {
            clearLoadTimeout();
            clearExportLoadTimeout();
        };
    }, [period]);

    return (
        <>
            <VatDeclaration
                onFetch={onClickCalculate}
                onCloseInfo={onCloseInfo}
                onChangePeriod={onChangePeriod}
                onRecalculate={onRecalculate}
                onDownloadInvoice={onDownloadInvoice}
                onNavigateToProducts={onNavigateToProducts}
                onToggleMail={onToggleMail}
                onDownloadExport={onDownloadExport}
                onExportDeclaration={onExportDeclaration}
                declarationItems={declarationItems}
                period={period}
                loadingExport={loadingExport}
                loading={loading || processStatus !== null}
                processStatus={processStatus}
                invalidated={invalidated}
                showInfo={showInfo}
                defaultVats={defaultVats}
                exportProcesses={exportProcesses}
            />
            <FirstUseNotifier />
        </>
    );
}
